import { isRiver } from '@1delta/lender-registry'
import type { TermAdapter } from './index'

/**
 * Redemption terms audited in CDP_REDEMPTION_TERMS.md.
 *
 * River (Satoshi Protocol) — a Prisma-lineage CDP with **no ongoing interest**
 * at all. The entire borrowing cost is a one-off decaying mint fee, so an APR
 * of 0 is not a free borrow and must never be rendered as one.
 */
export const riverAdapter: TermAdapter = {
  id: 'river',
  matches: isRiver,
  profileId: () => 'river.cdp@v1',
  build: (input) => {
    // A River deployment emits TWO rows per market and they are opposite
    // products, so a single `supply` block cannot describe both:
    //
    //  - the COLLATERAL row (`collateralActive: true`) is trove collateral. It
    //    earns nothing and cannot be opened on its own — `openTrove` refuses
    //    anything under `minNetDebt`, so posting collateral without borrowing
    //    is not expressible.
    //  - the DEBT row (`collateralActive: false`) is the per-chain Stability
    //    Pool. That IS an earn position, it absorbs liquidations (hence the
    //    bad-debt risk), and it opens perfectly well on its own.
    //
    // Describing the collateral row as a Stability-Pool deposit — which this
    // adapter did unconditionally — told a holder their collateral was an
    // instant-exit yield position carrying someone else's bad debt.
    const isCollateralRow = input.collateralActive !== false

    return {
      supply: isCollateralRow
        ? {
            role: 'collateral',
            // The trove is the position; collateral cannot exist without
            // `minNetDebt` of debt behind it, so this side never opens alone.
            availability: { opensWith: 'both-legs' },
            counterparty: { kind: 'cdp', solvency: 'overcollateralized' },
          }
        : {
            // The Stability Pool: an earn position, never collateral, and
            // openable on its own — `provideToSP` needs no trove.
            role: 'yield',
            exit: { mode: 'instant', settlement: 'sync', partialAllowed: true },
            availability: { opensWith: 'standalone' },
            counterparty: { kind: 'cdp', solvency: 'overcollateralized' },
            principal: { protected: false, risks: ['bad-debt'] },
          },
      borrow: {
        // Opening a trove takes collateral AND debt in one call, so a bare
        // borrow has no position to draw against. Only reaches the debt row —
        // the collateral row has no borrow side for the patch to refine.
        availability: { opensWith: 'both-legs' },
        rate: { kind: 'zero-interest', isLocked: true, compounding: 'none' },
        debtShape: 'accruing',
        exit: {
          earlyRepay: 'free',
          atMaturityCost: 'accrued',
          lateBehaviour: 'none',
          partialAllowed: true,
        },
        liquidation: {
          trigger: 'price',
          seizure: 'proportional',
          redeemable: true,
          redemption: {
            // Not governance and not a liquidation: any holder of the stable can
            // trigger it, without permission and without targeting anyone.
            trigger: 'permissionless-arbitrage',
            // Permissionless does NOT mean constant: it only pays the redeemer
            // while the stable trades under target, which is what makes it a peg
            // defence rather than a standing claim on collateral.
            driver: 'below-peg',
            // COLLATERAL-RATIO ordered, V1-style — NOT rate-ordered.
            //
            // This said `lowest-rate-first`, copied from the Liquity V2 shape,
            // and it was wrong twice over: River is Prisma/V1 lineage, which
            // redeems against the LOWEST-COLLATERALISED troves first, and River
            // charges no interest at all (`rate.kind: 'zero-interest'` above), so
            // the accompanying "raise your rate" advice pointed at a control that
            // does not exist on this protocol. A borrower following it would have
            // done nothing while sitting first in the real queue.
            order: 'lowest-collateral-ratio',
            // Deliberately ABSENT rather than `usd-neutral`. The V1 mechanism
            // sends collateral above the redeemed debt to a surplus balance that
            // the borrower must CLAIM, so whether they end up whole depends on an
            // action they may not know to take — see `defence`.
            defence:
              'Add collateral or repay to raise your collateral ratio and move behind other troves in the queue. If you are redeemed, claim the leftover collateral from the surplus balance — it is not returned automatically.',
          },
        },
        counterparty: { kind: 'cdp', solvency: 'overcollateralized' },
      },
      constraints: {
        // River troves are ADDRESS-keyed — there is no id to discover.
        positionModel: 'account',
        positionIdMeaning: 'Troves are keyed by owner address; there is no id.',
      },
    }
  },
}
